// Global, lightweight scroll-reveal. Observes any element with [data-reveal]
// and adds .is-visible the first time it enters the viewport. Re-scans the
// DOM via MutationObserver so SPA navigations and lazy content are covered.
// Respects prefers-reduced-motion.
//
// Additional helpers:
//  - [data-reveal-stagger]  → children animate with cascading delay (CSS).
//  - [data-reveal-deep]     → auto-tag inner block elements (headings, grid
//    items, cards, images, paragraphs) as individual data-reveal targets so
//    a section animates piece-by-piece while you scroll instead of all at
//    once. Safe for any wrapper.

let io: IntersectionObserver | null = null;
const observed = new WeakSet<Element>();

const DEEP_SELECTOR = [
  ":scope h1",
  ":scope h2",
  ":scope h3",
  ":scope > * > h1",
  ":scope > * > h2",
  ":scope > * > h3",
  ":scope .grid > *",
  ":scope [class*='rounded-2xl']",
  ":scope [class*='rounded-3xl']",
  ":scope figure",
  ":scope blockquote",
  ":scope > * > p",
  ":scope > * > img",
  ":scope > * > a",
].join(",");

function ensureObserver() {
  if (io) return io;
  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          (e.target as HTMLElement).classList.add("is-visible");
          io!.unobserve(e.target);
        }
      }
    },
    // threshold 0 + small bottom margin: triggers as soon as any pixel enters,
    // so sections taller than the mobile viewport (e.g. legal pages) still reveal.
    { threshold: 0, rootMargin: "0px 0px -80px 0px" }
  );
  return io;
}

function observe(el: HTMLElement) {
  if (observed.has(el)) return;
  observed.add(el);
  // Let IntersectionObserver handle visibility — it fires synchronously
  // for elements already in view, without forcing a layout reflow.
  ensureObserver().observe(el);
}

function expandDeep(root: ParentNode) {
  root.querySelectorAll<HTMLElement>("[data-reveal-deep]").forEach((wrap) => {
    if (wrap.dataset.revealDeepExpanded === "1") return;
    wrap.dataset.revealDeepExpanded = "1";
    const inners = wrap.querySelectorAll<HTMLElement>(DEEP_SELECTOR);
    inners.forEach((el) => {
      // Avoid double-tagging if a parent is already a reveal target.
      if (el.closest("[data-reveal]")) return;
      el.setAttribute("data-reveal", "");
    });
  });
}

function scan(root: ParentNode = document) {
  expandDeep(root);
  root.querySelectorAll<HTMLElement>("[data-reveal]").forEach(observe);
}

export function initScrollReveal() {
  if (typeof window === "undefined") return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) {
    document
      .querySelectorAll("[data-reveal], [data-reveal-deep]")
      .forEach((el) => el.classList.add("is-visible"));
    return;
  }
  const start = () => {
    scan();
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) scan(n as Element);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
